/**
 * Manifest da origem de mídia autorizada (PLAN §24).
 *
 * Formato:
 *
 * ```json
 * {
 *   "version": 1,
 *   "generatedAt": "2026-08-10T12:00:00.000Z",
 *   "items": {
 *     "ccb-sign-abc12345678": { "url": "/media/ab/hash.mp4", "bytes": 1234567, "sha256": "…" }
 *   }
 * }
 * ```
 *
 * O ganho de ter o manifest, em vez de só a convenção `<base>/<signId>.mp4`, é o
 * `bytes` e o `sha256`: com eles o download deixa de ser "o fetch resolveu" e passa
 * a ser verificável (PLAN §39). Sem manifest, a convenção continua funcionando.
 */
import { z } from 'zod';

export const mediaManifestSchema = z.object({
  version: z.int().positive(),
  generatedAt: z.iso.datetime({ offset: false }).optional(),
  items: z.record(
    z.string().min(1),
    z.object({
      url: z.string().min(1),
      bytes: z.int().positive().optional(),
      sha256: z
        .string()
        .regex(/^[0-9a-f]{64}$/i, 'sha256 deve ser hexadecimal de 64 caracteres')
        .optional(),
      mimeType: z.string().min(1).optional(),
    }),
  ),
});

export type MediaManifest = z.infer<typeof mediaManifestSchema>;
export type MediaManifestItem = MediaManifest['items'][string];

export const SUPPORTED_MEDIA_MANIFEST_VERSION = 1;

export class MediaManifestError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'MediaManifestError';
  }
}

/**
 * Busca e valida o manifest de mídia.
 *
 * Devolve `null` quando não há origem configurada ou o manifest não existe — ausência
 * é um estado normal, não erro: o app cai para a convenção de nome ou segue só online.
 */
export async function fetchMediaManifest(
  baseUrl: string,
  signal?: AbortSignal,
): Promise<MediaManifest | null> {
  if (!baseUrl) return null;

  const url = `${baseUrl.replace(/\/$/, '')}/manifest.json`;

  let payload: unknown;
  try {
    const response = await fetch(url, { cache: 'no-cache', ...(signal ? { signal } : {}) });
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new MediaManifestError(`Manifest de mídia respondeu HTTP ${response.status}`);
    }
    payload = await response.json();
  } catch (error) {
    if (error instanceof MediaManifestError) throw error;
    // Rede indisponível não é erro de configuração.
    return null;
  }

  const parsed = mediaManifestSchema.safeParse(payload);
  if (!parsed.success) {
    throw new MediaManifestError('Manifest de mídia é inválido', { cause: parsed.error });
  }

  if (parsed.data.version > SUPPORTED_MEDIA_MANIFEST_VERSION) {
    throw new MediaManifestError(
      `Manifest de mídia usa versão ${parsed.data.version}; esta aplicação entende ` +
        `no máximo ${SUPPORTED_MEDIA_MANIFEST_VERSION}.`,
    );
  }

  return parsed.data;
}

/** Resolve a URL do item contra a base, aceitando caminho relativo ou absoluto. */
export function resolveItemUrl(baseUrl: string, item: MediaManifestItem): string {
  if (/^https?:\/\//i.test(item.url)) return item.url;
  const base = baseUrl.replace(/\/$/, '');
  return `${base}/${item.url.replace(/^\//, '')}`;
}
